import React, { useState, useEffect } from "react";
import { Typography, Box, FormControlLabel, Checkbox } from '@mui/material';
import * as taskTemplateStyle from 'mui-components/kart/fieldmap/tasktemplates/TaskTemplateStyle';
import MuiTextField from 'components/mui3-forms/MuiTextField';
import MuiSelectDropdown from 'components/mui3-forms/MuiSelectDropdown';
import MuiNestedDropDown from "components/mui3-forms/MuiNestedDropdown";
import { pesticideUnitOptions } from "utils/DynamicFields";
import MuiMultiSelect from 'components/mui-forms/MuiMultiSelect';
import MuiTextFieldArea from 'components/mui3-forms/MuiTextFieldArea';
import _ from 'lodash';

const AddTaskWaterJournalTemplate1 = (props) => {
  const { intl, fieldMasterData, activitiesCommon, onFieldChange, formData, handleNumericValidation, formErrors, initialData } = props;
  let pesticideDropdowns = _.filter(activitiesCommon, ['activityId', 'act19'])[0].dropdowns[2].elements;
  const [selectedPesticideTypes, setSelectedPesticideTypes] = useState([]);
  const [isChecked, setIsChecked] = useState(false);
  const [distanceChecked, setDistanceChecked] = useState(false);
  const [vegetationChecked, setVegetationChecked] = useState(false);
  const selectedColor = '#00696D';

  useEffect(() => {
    if (formData.pesticideType?.length > 0) {
      setSelectedPesticideTypes(formData.pesticideType);
    }
  }, [formData.pesticideType]);

  useEffect(() => {
    if (formData.pesticideType?.length > 0) {
      const updatedFormData = { ...formData };
      formData.pesticideType.forEach((_, index) => {
        const amountKey = `pesticideType.${index}.amount`;
        const unitKey = `pesticideType.${index}.unit`;
        updatedFormData[amountKey] = formData[amountKey];
        updatedFormData[unitKey] = formData[unitKey];
      });
      setSelectedPesticideTypes(formData.pesticideType);
    }
  }, [formData.pesticideType]);

  let pestDataValue = {
    element: formData?.taskActivity?.pestData,
    leaves: formData?.taskActivity['pestSubList'] || null
  }

  const handleChange = (event) => {
    setIsChecked(event.target.checked);
  };

  const handleDistanceChange = (event) => {
    setDistanceChecked(event.target.checked);
  };

  const handleVegetationChange = (event) => {
    setVegetationChecked(event.target.checked);
  };

  const handlePesticideTypeChange = (event) => {
    setSelectedPesticideTypes(event.target.value);
    if (onFieldChange) {
      onFieldChange('pesticideType', event.target.value);
    }
  };

  // Effect of pesticide application
  const getEffectOfApplicationValues = () => {
    const values = ['God', 'Ok', 'Dårlig'];
    return values.map(value => ({
      value,
      label: value,
    }));
  };

  // Crop List
  const getDropDownFieldsStatuses = dropdownList => {
    return dropdownList?.map(item => ({
      value: item.name,
      label: item.name
    }));
  };

  // lessThan2PCSlope
  const lessThanTwoPercentSlope = () => {
    return [
      { value: 'true', label: 'Ja' },
      { value: 'false', label: 'Nei' }
    ];
  };

  // Pesticide type options
  const pesticideTypeOptions = (activitiesCommon) => {
    const pesticideActivity = activitiesCommon?.find(
      (activity) => activity.activityId === 'act19'
    );
    const pesticideDropdown = pesticideActivity?.dropdowns?.find(
      (dropdown) => dropdown.element === 'pesticide'
    );
    return pesticideDropdown?.leaves || [];
  };

  return (
    <Box sx={{
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#A8A8A8',
        borderRadius: '4px',
        transition: 'background-color 0.3s ease',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#B8B8B8',
      }
    }}
    >
      <Box sx={{ display: "flex", gap: '14px', paddingTop: '24px', alignItems: "center" }}>
        {/* Nozzle name */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "NOZZLE_NAME_WATER_JOURNAL" })}
          defaultValue={formData.waterJournal.nozzleName ?? ''}
          fieldName='nozzleName'
          onChange={onFieldChange}
          required={true}
          errorHelperText={formErrors['nozzleName']}
          activityId="act19"
        />
        {/* Nozzle factory */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'NOZZLE_FACTORY_WATER_JOURNAL' })}
          defaultValue={formData.waterJournal.nozzleFactory ?? ''}
          fieldName='nozzleFactory'
          onChange={onFieldChange}
          required={true}
          errorHelperText={formErrors['nozzleFactory']}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", gap: '10px', alignItems: "center" }}>
        {/* Nozzle height */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "NOZZLE_HEIGHT_WATER_JOURNAL" })}
          defaultValue={formData.waterJournal.nozzleHeight ?? ''}
          fieldName='nozzleHeight'
          onChange={handleNumericValidation}
          required={true}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_CM' })}
          errorHelperText={formErrors['nozzleHeight']}
          activityId="act19"
        />
        {/* Pressure */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'PRESSURE_WATER_JOURNAL' })}
          defaultValue={formData.waterJournal.pressure ?? ''}
          fieldName='pressure'
          required={true}
          onChange={handleNumericValidation}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_BAR' })}
          errorHelperText={formErrors['pressure']}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", gap: '16px', alignItems: "center" }}>
        {/* Drift reduction */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'WATER_JOURNAL_DRIFT_REDUCTION' })}
          defaultValue={formData.waterJournal.driftReduction ?? ''}
          fieldName='driftReduction'
          onChange={handleNumericValidation}
          autoFocus={false}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_PERCENTAGE' })}
          errorHelperText={formErrors['driftReduction']}
          required={true}
          activityId="act19"
        />
        {/* Max Driving speed */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'WATER_JOURNAL_DRIVING_SPEED' })}
          defaultValue={formData.waterJournal.maxDrivingSpeed ?? ''}
          fieldName='maxDrivingSpeed'
          onChange={handleNumericValidation}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_KM' })}
          autoFocus={false}
          errorHelperText={formErrors['maxDrivingSpeed']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '32px' }}>
        <Typography style={taskTemplateStyle.textHeadingStyle}>
          {intl.formatMessage({ id: 'FIELD_DETAILS' })}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: '16px', paddingTop: '24px', alignItems: "center" }}>
        {/* Crop */}
        <MuiSelectDropdown
          defaultLabel={`${intl.formatMessage({ id: 'CT_CR' })}*`}
          defaultValue={formData.taskActivity?.crop || initialData.taskActivity?.crop || ''}
          fieldName='crop'
          options={getDropDownFieldsStatuses(fieldMasterData.cropList)}
          autoFocus={false}
          required={true}
          onChange={onFieldChange}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: '250px', // Adjust this value (in pixels) to your desired height
              },
            },
          }}
          activityId="act19"
          showEmptySpace={false}
        />
        {/* less than 2 percent slope */}
        <MuiSelectDropdown
          defaultLabel={`${intl.formatMessage({ id: 'PC_SLOPE_WATER_JOURNAL' })}*`}
          defaultValue={String(formData.waterJournal.lessThan2PCSlope ?? '')}
          fieldName='lessThan2PCSlope'
          options={lessThanTwoPercentSlope()}
          required={true}
          onChange={onFieldChange}
          activityId="act19"
          showEmptySpace={false}
          autoFocus={false}
        />
      </Box>
      <Box sx={{ display: "flex", gap: '16px', paddingTop: '24px', alignItems: "center" }}>
        {/* distance to water body */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "WATER_JOURNAL_DISTANCE_TO_WATER_BODY" })}
          defaultValue={formData.waterJournal.distanceToWaterBody ?? ''}
          fieldName='distanceToWaterBody'
          onChange={handleNumericValidation}
          autoFocus={false}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          errorHelperText={formErrors['distanceToWaterBody']}
          required={true}
          activityId="act19"
        />
        {/* distance to water well */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'WATER_WELL_DISTANCE_WATER_JOURNAL' })}
          defaultValue={formData.waterJournal.distanceToWaterWell ?? ''}
          fieldName='distanceToWaterWell'
          onChange={handleNumericValidation}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          autoFocus={false}
          errorHelperText={formErrors['distanceToWaterWell']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* vegetated buffer exists */}
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.waterJournal?.vegetatedBufferExists ?? false}
              onChange={(e) => {
                const checked = e.target.checked;
                handleChange(e); // If you need to update `isChecked`
                onFieldChange('vegetatedBufferExists', checked); // Call your field change handler
              }}
              sx={{
                color: isChecked ? selectedColor : null,
                '&.Mui-checked': {
                  color: selectedColor,
                },
              }}
            />
          }
          label={`${intl.formatMessage({ id: 'ESTABLISHED_BUFFER_ZONE' })}?`}
          sx={{ ...taskTemplateStyle.modalDialogSubTitle, marginRight: '0px' }}
        />
      </Box>
      <Box sx={{ display: "flex", gap: '16px', paddingTop: '24px', alignItems: "center" }}>
        {/* New field - 26 bufferzonePlacement*/}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "LOCATION_ON_BUFFER_ZONE" })}
          defaultValue={formData.waterJournal.bufferZonePlacement ?? ''}
          fieldName='bufferZonePlacement'
          onChange={onFieldChange}
          autoFocus={false}
          required={isChecked ? true : false}
          errorHelperText={formErrors['bufferZonePlacement']}
          disabled={!(formData.waterJournal?.vegetatedBufferExists ?? false)} // Disable based on isChecked
          activityId="act19"
        />
        {/* locationAndDistance */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'LENGTH_BUFFER_ZONE' })}
          defaultValue={formData.waterJournal.locationAndDistance ?? ''}
          fieldName='locationAndDistance'
          onChange={handleNumericValidation}
          autoFocus={false}
          required={isChecked ? true : false}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          errorHelperText={formErrors['locationAndDistance']}
          disabled={!(formData.waterJournal?.vegetatedBufferExists ?? false)} // Disable based on isChecked
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '32px' }}>
        <Typography style={taskTemplateStyle.textHeadingStyle}>
          {intl.formatMessage({ id: 'PESTICIDE_INFO' })}
        </Typography>
      </Box>
      <Box sx={{ width: '100%', display: "flex", flexDirection: 'column', alignItems: "flex-start", paddingTop: '24px' }}>
        <MuiMultiSelect
          label={intl.formatMessage({ id: 'TYPE_OF_PESTICIDE' })}
          value={selectedPesticideTypes}
          defaultValue={selectedPesticideTypes}
          name='pesticideType.name'
          options={pesticideTypeOptions(activitiesCommon)}
          onChange={handlePesticideTypeChange}
          autoFocus={false}
          fullWidth={true}
          required={true}
          errorHelperText={formErrors['pesticideType.name']}
          customSx={{
            width: '100%',
            marginLeft: 0,
            '& .MuiFormControl-root': {
              width: '100% !important',
              marginLeft: 0,
            },
            '& .MuiOutlinedInput-root': {
              width: '100% !important',
            },
            '& .MuiSelect-select': {
              width: '100% !important',
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: '250px', // Adjust this value (in pixels) to your desired height
              },
            },
          }}
        />
        {selectedPesticideTypes.map((pesticideName, index) => {
          const fieldKey = `pesticideType[${pesticideName || 'default'}]`;
          return (
            <Box
              key={fieldKey}
              sx={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '24px', width: '100%' }}
            >
              <Typography variant="subtitle1">{pesticideName}</Typography>
              <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', width: '100%' }}>
                {/* Pesticide Amount */}
                <MuiTextField
                  defaultLabel={intl.formatMessage({ id: 'AMOUNT_OF_PESTICIDE' })}
                  defaultValue={formData[`pesticideType.${index}.amount`] || ''}
                  fieldName={`pesticideType.${index}.amount`}
                  onChange={handleNumericValidation}
                  autoFocus={false}
                  required={true}
                  errorHelperText={formErrors[`pesticideType.${index}.amount`]}
                  fullWidth
                  activityId="act19"
                />
                {/* Pesticide Unit */}
                <MuiSelectDropdown
                  defaultLabel={`${intl.formatMessage({ id: 'CT_UNIT' })}*`}
                  defaultValue={formData[`pesticideType.${index}.unit`] || ''}
                  fieldName={`pesticideType.${index}.unit`}
                  options={pesticideUnitOptions()}
                  required={true}
                  autoFocus={false}
                  fullWidth
                  onChange={onFieldChange}
                  activityId="act19"
                />
              </Box>
            </Box>
          );
        })}

        {selectedPesticideTypes.length === 0 && (
          <Box sx={{ display: 'flex', gap: '16px', paddingTop: '24px', alignItems: 'center', width: '100%' }}>
            {/* Pesticide Amount - Shown when no pesticide types are selected */}
            <MuiTextField
              defaultLabel={intl.formatMessage({ id: 'AMOUNT_OF_PESTICIDE' })}
              defaultValue={formData.pesticideType?.default?.amount || ''}
              fieldName="pesticideType.0.amount"
              options={pesticideUnitOptions()}
              onChange={handleNumericValidation}
              required={true}
              autoFocus={false}
              fullWidth
              errorHelperText={formErrors['pesticideType.0.amount']}
              activityId="act19"
            />
            {/* Pesticide Unit - Shown when no pesticide types are selected */}
            <MuiSelectDropdown
              defaultLabel={`${intl.formatMessage({ id: 'CT_UNIT' })}*`}
              defaultValue={formData.pesticideType?.default?.unit || ''}
              fieldName="pesticideType.0.unit"
              options={pesticideUnitOptions()}
              required={true}
              autoFocus={false}
              fullWidth
              onChange={onFieldChange}
              activityId="act19"
            />
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Pest including subtypes - what attribute ?*/}
        <MuiNestedDropDown
          label={`${intl.formatMessage({ id: 'PEST_SUBTYPE' })}*`}
          options={pesticideDropdowns}
          defaultValue={pestDataValue}
          onChange={(value) => onFieldChange('pestInfo', value)}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', paddingTop: '24px', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '24px' }}>
            {/* New field - 28 distanceOfSurfaceWaterExists */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.waterJournal?.distanceOfSurfaceWaterExists ?? false}
                  onChange={(e) => {
                    const checked = e.target.checked
                    handleDistanceChange(e)
                    onFieldChange('distanceOfSurfaceWaterExists', checked)
                  }}
                  sx={{
                    paddingRight: '0px',
                    color: distanceChecked ? selectedColor : null,
                    '&.Mui-checked': {
                      color: selectedColor,
                    },
                  }}
                />
              }
              label={intl.formatMessage({ id: 'DISTANCE_REQUIREMENTS_SURFACE_WATER' })}
              sx={{ ...taskTemplateStyle.modalDialogSubTitle, whiteSpace: 'nowrap' }}
            />
            {/* New field - 30 distanceOfSurfaceWater */}
            <MuiTextField
              defaultLabel={intl.formatMessage({ id: 'DISTANCE_REQUIREMENTS' })}
              defaultValue={formData.waterJournal.distanceOfSurfaceWater ?? ''}
              fieldName="distanceOfSurfaceWater"
              onChange={handleNumericValidation}
              autoFocus={false}
              inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
              error={!!formErrors['distanceOfSurfaceWater']}
              helperText={formErrors['distanceOfSurfaceWater']}
              sx={taskTemplateStyle.checkboxSelectedAttributesStyle}
              required={distanceChecked ? true : false}
              disabled={!(formData.waterJournal?.distanceOfSurfaceWaterExists ?? false)} // Disable based on distanceChecked
              activityId="act19"
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '24px' }}>
            {/* isVegitatedBufferzone */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.waterJournal?.vegitatedBufferZone ?? false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    handleVegetationChange(e)
                    onFieldChange('vegitatedBufferZone', checked);
                  }}
                  sx={{
                    paddingRight: '0px',
                    color: vegetationChecked ? selectedColor : null,
                    '&.Mui-checked': {
                      color: selectedColor,
                    },
                  }}
                />
              }
              label={intl.formatMessage({ id: 'VEGETATED_BUFFER_ZONE_REQUIREMENTS' })}
              sx={{ ...taskTemplateStyle.modalDialogSubTitle, whiteSpace: 'nowrap' }}
            />
            {/* New field - 31 distanceOfVegitagedBufferZone */}
            <MuiTextField
              defaultLabel={intl.formatMessage({ id: 'BUFFER_ZONE_REQUIREMENTS' })}
              defaultValue={formData.waterJournal.distanceOfVegitagedBufferZone ?? ''}
              fieldName="distanceOfVegitagedBufferZone"
              onChange={handleNumericValidation}
              autoFocus={false}
              inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
              error={!!formErrors['distanceOfVegitagedBufferZone']}
              helperText={formErrors['distanceOfVegitagedBufferZone']}
              sx={taskTemplateStyle.checkboxSelectedAttributesStyle}
              required={vegetationChecked ? true : false}
              disabled={!((formData.waterJournal?.vegitatedBufferZone ?? false))}
              activityId="act19"
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '32px' }}>
        <Typography style={taskTemplateStyle.textHeadingStyle}>
          {intl.formatMessage({ id: 'AF_OTHER' })}
        </Typography>
      </Box>
      {/* Reason for pesticide application */}
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '24px' }}>
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'RFPA' })}
          defaultValue={formData.taskActivity?.reason || ''}
          fieldName="reason"
          onChange={onFieldChange}
          required={true}
          error={!!formErrors['reason']}
          helperText={formErrors['reason']}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Effect of application */}
        <MuiSelectDropdown
          defaultLabel={`${intl.formatMessage({ id: 'EFFECT' })}*`}
          defaultValue={formData.taskActivity?.effectApplication || ''}
          fieldName='effectApplication'
          options={getEffectOfApplicationValues()}
          autoFocus={false}
          required={true}
          onChange={onFieldChange}
          activityId="act19"
          showEmptySpace={false}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '56px' }}>
        <MuiTextFieldArea
          defaultLabel={intl.formatMessage({ id: 'NOTE' })}
          defaultValue={formData.note || ''}
          fieldName='note'
          onChange={onFieldChange}
        />
      </Box>
    </Box>
  );
};

export default AddTaskWaterJournalTemplate1;